import { registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { Emitter } from "../../../base/common/events.js";
import { IInspectionService, type InspectionTargetRef } from "../../inspection/common/inspection.js";
import { IPropertyService, PropertyScope, PropertyViewMode } from "../../properties/common/properties.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { ILogService } from "../../log/common/log.js";
import { ILicenseService } from "../../auth/common/licenses.js";
import { ICodeViewService, type CodeOption } from "./codeView.js";

/**
 * See {@link ICodeViewService}. A thin subscriber of the inspection service's
 * target layers: whatever {@link IInspectionService.getTargets} currently says
 * (selection, Resources click, node-graph drill-down, clicked function node) is
 * what this shows — the service holds NO follow/override policy of its own,
 * only per-panel concerns: lock (freeze what's shown) and consumer visibility
 * (skip the walk while the tab is hidden).
 *
 * For each target it triggers a property walk and holds the options that walk
 * reports (via {@link PropertyService}'s soft-coupled hook); targets can also
 * carry explicit options in `payloads.code` (e.g. a TSL Fn body the property
 * system can't describe). The last *picked option* stays selected across
 * target changes (exact label first, then its kind).
 */
export class CodeViewService extends Disposable implements ICodeViewService {
    declare readonly _serviceBrand: undefined;

    private readonly _onDidChange = this._register(new Emitter<void>());
    readonly onDidChange = this._onDidChange.event;

    /** The effective targets currently shown (guards stray reportOptions). */
    private _targets: InspectionTargetRef[] = [];
    /** Options as reported per target; merged into {@link _options} in target order. */
    private readonly _reported = new Map<object, CodeOption[]>();
    private _options: CodeOption[] = [];
    private _selectedId: string | null = null;
    /**
     * Sticky pick, remembered across selections: the exact option (label) the
     * user chose, and its role (kind) as fallback. Picking "ShaderMaterial" then
     * selecting another object that also offers a "ShaderMaterial" option shows
     * that again; if only the *kind* matches (e.g. another material's fragment
     * shader), the kind wins over resetting to the first option.
     */
    private _preferredKind: string | null = null;
    private _preferredLabel: string | null = null;
    private _locked = false;
    private _consumerVisible = true;
    /** A selection-independent snippet is showing (see {@link showEphemeral}); the
     *  next real selection change dismisses it and restores the normal view. */
    private _ephemeralActive = false;

    get options(): readonly CodeOption[] { return this._options; }
    get selected(): CodeOption | null {
        return this._options.find(o => o.id === this._selectedId) ?? null;
    }
    get locked(): boolean { return this._locked; }

    constructor(
        @IInspectionService private readonly inspection: IInspectionService,
        @IPropertyService private readonly properties: IPropertyService,
        @IAnalyticsService private readonly analytics: IAnalyticsService,
        @ILicenseService private readonly license: ILicenseService,
        @ILogService private readonly logger: ILogService,
    ) {
        super();
        this._register(this.inspection.onDidTargetsChange(() => {
            if (this._locked) return; // frozen — resyncs on unlock
            this._ephemeralActive = false; // a real selection change dismisses the ephemeral snippet
            this._sync();
        }));
        this._sync();
    }

    /** Pull the current targets from the inspection service and (re)collect. */
    private _sync(): void {
        // Base = the raw selection: use only the PRIMARY target (walking every
        // owner of a large multi-select would fire N property walks). Overrides
        // are curated sets (a drill-down's materials) → show all of them.
        const targets = this.inspection.getTargets();
        const refs = this.inspection.hasOverride ? targets : targets.slice(0, 1);
        // Unchanged targets (e.g. an override was re-set to the same content, or
        // an unlock without changes) → skip, so nothing flashes/re-walks.
        if (refs.length === this._targets.length
            && refs.every((r, i) => r.target === this._targets[i].target
                && r.label === this._targets[i].label
                && !this._codeOptions(r) === !this._codeOptions(this._targets[i]))) return;
        this._applyTargets(refs);
    }

    /** Explicit code options a target carries (e.g. a TSL Fn body), if any. */
    private _codeOptions(ref: InspectionTargetRef): CodeOption[] | undefined {
        const code = ref.payloads?.code;
        return Array.isArray(code) && code.length ? (code as CodeOption[]) : undefined;
    }

    /** Show the given targets: adopt explicit options, walk the rest. */
    private _applyTargets(refs: InspectionTargetRef[]): void {
        this._targets = refs;
        this._reported.clear();
        this._options = [];
        this._selectedId = null;
        // Refs with explicit options don't need (and must not be clobbered by)
        // a property walk — a walk of a TSL node would report nothing.
        for (const ref of refs) {
            const explicit = this._codeOptions(ref);
            if (explicit) this._reported.set(ref.target, explicit);
        }
        this._rebuildOptions(); // also fires the change event
        // Trigger walks so the property service reports options for the rest.
        // Skipped while hidden — collected when we next become visible.
        if (this._consumerVisible) {
            for (const ref of refs) {
                if (!this._reported.has(ref.target)) this._collect(ref.target);
            }
        }
    }

    /** Free tier shows HALF of the inspector-GENERATED sources — compiled
     *  shaders and the reconstructed TSL output — enough to be genuinely
     *  useful and verifiably real, with the cut line announcing Pro. The
     *  trimmed half never reaches the DOM (nothing to un-hide). Authored
     *  shaders and three.js object/material/component source stay fully free. */
    private _gateCompiledShader(opt: CodeOption): CodeOption {
        const gated = opt.kind.startsWith("compiled-") || opt.kind === "tsl-code";
        if (!gated) return opt;
        const produce = opt.produce;
        return {
            ...opt,
            produce: async () => {
                const source = await produce();
                if (source == null) return source;
                if (this.license.isInspectorAccessAllowed) return source;
                const lines = source.split("\n");
                const keep = Math.max(10, Math.min(100, Math.ceil(lines.length / 2)));
                // Short sources fit entirely inside the minimum preview — there
                // is nothing to cut, so no banner ("showing 10 of 4 lines").
                if (keep >= lines.length) return source;
                this.analytics?.logEvent("codeview.compiled_trimmed", { kind: opt.kind, lines: lines.length });
                const rule = "// " + "─".repeat(64);
                return lines.slice(0, keep).join("\n") + `

${rule}
//
//   ✂  Preview ends here — showing ${keep} of ${lines.length} lines.
//
//   Needle Inspector Pro unlocks the rest, and everything else:
//
//   →  Full compiled shaders & reconstructed TSL, always
//   →  Live-edit materials, lights & transforms on your own projects
//   →  Every resource with memory estimates — find the leaks
//   →  AI editing via MCP: Claude Code, Cursor & co.
//   →  Keep your work — download changesets, export GLB
//
//   One-time purchase, no subscription — included with Needle Engine Pro.
//
//   ★  Get Pro in the toolbar.
//
${rule}


`;
            },
        };
    }

    /** Fire-and-forget property walk; options arrive asynchronously via reportOptions. */
    private _collect(owner: object): void {
        // Same options the inspector uses, so a concurrent walk is shared (the
        // property service dedupes in-flight calls per object).
        this.properties.getProperties(owner, { scope: PropertyScope.INSPECTOR, viewMode: PropertyViewMode.ALL })
            .catch(err => this.logger.warn(`[codeview] property walk failed: ${err instanceof Error ? err.message : err}`));
    }

    showEphemeral(option: CodeOption): void {
        // Not tied to any inspection target: drop the target set so the next real
        // selection change re-syncs and dismisses this. No property walk, no gate.
        this._ephemeralActive = true;
        this._targets = [];
        this._reported.clear();
        this._options = [option];
        this._selectedId = option.id;
        this._onDidChange.fire();
    }

    reportOptions(target: object, options: CodeOption[]): void {
        if (this._ephemeralActive) return; // a stray walk must not clobber the snippet
        const index = this._targets.findIndex(r => r.target === target);
        if (index < 0) return; // a walk for something we're not showing
        // Never clobber a target's explicit options with a (useless) walk result.
        if (this._codeOptions(this._targets[index])) return;
        this._reported.set(target, options);
        this._rebuildOptions();
    }

    /**
     * Merges the per-target reports (in target order → one picker section per
     * graph) and re-resolves the selection. With several targets, ids get an
     * index prefix (two identical materials report identical ids) and the ref's
     * label overrides the group, so sections carry the graph titles.
     */
    private _rebuildOptions(): void {
        const multi = this._targets.length > 1;
        const merged: CodeOption[] = [];
        this._targets.forEach((ref, i) => {
            for (const raw of this._reported.get(ref.target) ?? []) {
                const opt = this._gateCompiledShader(raw);
                merged.push(multi
                    ? { ...opt, id: `g${i}:${opt.id}`, group: ref.label ?? opt.group }
                    : opt);
            }
        });
        this._options = merged;
        // Sticky selection, most- to least-specific: the exact option picked
        // last (label + kind, e.g. "ShaderMaterial" on the new object too), then
        // the same role (kind), then the currently selected id, then the first.
        const byLabel = this._preferredLabel
            ? merged.find(o => o.label === this._preferredLabel && o.kind === this._preferredKind)
            : undefined;
        const byKind = this._preferredKind ? merged.find(o => o.kind === this._preferredKind) : undefined;
        const byId = this._selectedId ? merged.find(o => o.id === this._selectedId) : undefined;
        this._selectedId = (byLabel ?? byKind ?? byId ?? merged[0])?.id ?? null;
        this._onDidChange.fire();
    }

    select(id: string): void {
        const opt = this._options.find(o => o.id === id);
        if (!opt) return;
        this._selectedId = id;
        // Remember the exact pick + its role for future selections.
        this._preferredKind = opt.kind;
        this._preferredLabel = opt.label;
        // What people actually read: the source role + the owner's kind and
        // CLASS name (never instance names — see CodeOption.ownerType).
        this.analytics?.logEvent("codeview.select", {
            kind: opt.kind,
            category: opt.category,
            ownerType: opt.ownerType,
        });
        this._onDidChange.fire();
    }

    toggleLock(): void {
        this._locked = !this._locked;
        // Unlock resyncs to whatever the inspection targets are now.
        if (!this._locked) this._sync();
        this._onDidChange.fire();
    }

    setConsumerVisible(visible: boolean): void {
        if (this._consumerVisible === visible) return;
        this._consumerVisible = visible;
        // Becoming visible with pending, uncollected targets → collect them now.
        if (visible) {
            for (const ref of this._targets) {
                if (!this._reported.has(ref.target)) this._collect(ref.target);
            }
        }
    }
}

// Eager (false): injected into Application at startup like the node-graph service.
registerSingleton(ICodeViewService, new SyncDescriptor(CodeViewService, [], false));
